/**
 * Domain trajectory — pure data shaping (DC-1658, v2.5 Layer 3, surface b).
 *
 * Transforms the domain readiness history (GET /api/domains/:id/readiness/history)
 * + checkpoints (GET /api/domains/:id/checkpoints) into the series, annotations,
 * delta chips, coverage line, and a11y table the DomainTrajectoryChart renders.
 *
 * No DOM, no ApexCharts — unit-testable in isolation. Scores are stored on the
 * FAIR engine native [0,5] scale; this module scales to the 0–100 display axis
 * (×20) so a delta reads "Technical +12", not "+0.6". Coherence is [0,1] → ×100.
 *
 * Honesty contracts carried through (DC-1652): a scoring_version change between
 * two adjacent readings is surfaced as a rubric-change marker so "data improved"
 * and "scorer changed" stay visually distinct.
 */

export interface DomainSnapshotDTO {
  id: string;
  captured_at: string;
  technical_composite: number | null;
  perceived_composite: number | null;
  documented_composite: number | null;
  overall_composite: number | null;
  mean_coherence: number | null;
  dataset_count: number;
  interview_count: number;
  document_count: number;
  scoring_version: string;
  trigger: string;
}

export interface CheckpointDTO {
  id: string;
  label: string;
  checkpoint_at: string;
}

export type LegKey = 'technical' | 'perceived' | 'governed' | 'composite';

export interface TrajectoryPoint {
  x: number;
  y: number | null;
}

export interface TrajectorySeries {
  key: LegKey;
  name: string;
  data: TrajectoryPoint[];
}

export interface RubricChangeMarker {
  x: number;
  version: string;
}

export interface CheckpointMarker {
  x: number;
  label: string;
}

/**
 * The reading at which a triangulation leg first becomes present (its composite
 * goes null → non-null). Surfaced as an annotation so a composite dip caused by
 * ADDING a leg's evidence reads as "more coverage now", not "readiness fell"
 * (same honesty discipline as the rubric-change marker).
 */
export interface LegEntryMarker {
  x: number;
  /** 'Technical' | 'Perceived' | 'Governed' */
  leg: string;
}

export interface DeltaChip {
  key: LegKey;
  name: string;
  /** current − baseline on the 0–100 axis; null when either endpoint is null. */
  delta: number | null;
}

export interface TrajectoryTableRow {
  date: string;
  checkpoint: string | null;
  technical: number | null;
  perceived: number | null;
  governed: number | null;
  composite: number | null;
  coherence: number | null;
  datasets: number;
  interviews: number;
  documents: number;
  rubricVersion: string;
}

export interface DomainTrajectoryModel {
  isEmpty: boolean;
  isSinglePoint: boolean;
  series: TrajectorySeries[];
  rubricChanges: RubricChangeMarker[];
  checkpoints: CheckpointMarker[];
  legEntries: LegEntryMarker[];
  deltaChips: DeltaChip[];
  coverage: { datasets: number; interviews: number; documents: number; lowCoverage: boolean };
  table: TrajectoryTableRow[];
}

/** Below this many contributing datasets, a domain reading is flagged low-coverage. */
export const LOW_COVERAGE_DATASETS = 3;

const LEG_META: Array<{ key: LegKey; name: string; field: keyof DomainSnapshotDTO }> = [
  { key: 'technical', name: 'Technical', field: 'technical_composite' },
  { key: 'perceived', name: 'Perceived', field: 'perceived_composite' },
  { key: 'governed', name: 'Governed', field: 'documented_composite' },
  { key: 'composite', name: 'Composite', field: 'overall_composite' },
];

/** [0,5] native → 0–100 display, rounded to an integer; null passes through. */
function to100(value: number | null): number | null {
  return value === null ? null : Math.round(value * 20);
}

/** [0,1] coherence → 0–100 display; null passes through. */
function coherenceTo100(value: number | null): number | null {
  return value === null ? null : Math.round(value * 100);
}

function ms(iso: string): number {
  return new Date(iso).getTime();
}

export function buildDomainTrajectoryModel(
  history: DomainSnapshotDTO[],
  checkpoints: CheckpointDTO[],
): DomainTrajectoryModel {
  // Defensive sort oldest-first (the API already orders this way).
  const rows = [...history].sort((a, b) => ms(a.captured_at) - ms(b.captured_at));

  const isEmpty = rows.length === 0;
  const isSinglePoint = rows.length === 1;

  const series: TrajectorySeries[] = LEG_META.map((leg) => ({
    key: leg.key,
    name: leg.name,
    data: rows.map((r) => ({ x: ms(r.captured_at), y: to100(r[leg.field] as number | null) })),
  }));

  // Rubric-change markers: where scoring_version differs from the prior reading.
  const rubricChanges: RubricChangeMarker[] = [];
  for (let i = 1; i < rows.length; i += 1) {
    if (rows[i].scoring_version !== rows[i - 1].scoring_version) {
      rubricChanges.push({ x: ms(rows[i].captured_at), version: rows[i].scoring_version });
    }
  }

  const checkpointMarkers: CheckpointMarker[] = checkpoints.map((c) => ({
    x: ms(c.checkpoint_at),
    label: c.label,
  }));

  // Leg-entry markers: a leg that was absent at baseline and becomes present
  // later. Excludes the composite. If a leg is present from the first reading it
  // never "entered" (no marker). Only the first appearance is marked.
  const legEntries: LegEntryMarker[] = [];
  const presenceLegs: Array<{ name: string; field: keyof DomainSnapshotDTO }> = [
    { name: 'Technical', field: 'technical_composite' },
    { name: 'Perceived', field: 'perceived_composite' },
    { name: 'Governed', field: 'documented_composite' },
  ];
  for (const leg of presenceLegs) {
    const firstPresent = rows.findIndex((r) => (r[leg.field] as number | null) !== null);
    if (firstPresent > 0) {
      legEntries.push({ x: ms(rows[firstPresent].captured_at), leg: leg.name });
    }
  }

  // Delta chips: latest vs earliest reading (the chart's quick read; the progress
  // report is the precise checkpoint-anchored artifact).
  const baseline = rows[0];
  const current = rows[rows.length - 1];
  const deltaChips: DeltaChip[] = LEG_META.map((leg) => {
    if (isEmpty) return { key: leg.key, name: leg.name, delta: null };
    const b = to100(baseline[leg.field] as number | null);
    const c = to100(current[leg.field] as number | null);
    return { key: leg.key, name: leg.name, delta: b !== null && c !== null ? c - b : null };
  });

  const coverage = isEmpty
    ? { datasets: 0, interviews: 0, documents: 0, lowCoverage: true }
    : {
        datasets: current.dataset_count,
        interviews: current.interview_count,
        documents: current.document_count,
        lowCoverage: current.dataset_count < LOW_COVERAGE_DATASETS,
      };

  // Attach each checkpoint to the earliest reading at or after it (for the table).
  const checkpointForRow = new Map<string, string>();
  for (const cp of checkpoints) {
    const t = ms(cp.checkpoint_at);
    const row = rows.find((r) => ms(r.captured_at) >= t);
    if (row && !checkpointForRow.has(row.id)) checkpointForRow.set(row.id, cp.label);
  }

  const table: TrajectoryTableRow[] = rows.map((r) => ({
    date: r.captured_at.slice(0, 10),
    checkpoint: checkpointForRow.get(r.id) ?? null,
    technical: to100(r.technical_composite),
    perceived: to100(r.perceived_composite),
    governed: to100(r.documented_composite),
    composite: to100(r.overall_composite),
    coherence: coherenceTo100(r.mean_coherence),
    datasets: r.dataset_count,
    interviews: r.interview_count,
    documents: r.document_count,
    rubricVersion: r.scoring_version,
  }));

  return {
    isEmpty,
    isSinglePoint,
    series,
    rubricChanges,
    checkpoints: checkpointMarkers,
    legEntries,
    deltaChips,
    coverage,
    table,
  };
}
